<template>
  <span v-tooltip.bottom="relativeTimeTooltip">{{ formattedValue }}</span>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import type { PropType } from 'vue'
import { useCurrencyCode } from '@/composables/useCurrencyCode'
import { formatCurrency, formatNumber, formatDate, formatDateTime, formatRelativeTime, type FormatOptions } from '@/utils/formatters'
import { resolveCurrencyCode } from '@/utils/constants'

type FormatterType = 'currency' | 'datetime' | 'date' | 'relative-time' | 'number' | 'text' | 'list'

export default defineComponent({
  name: 'AM3Formatter',

  props: {
    value: {
      type: [String, Number, Array],
      default: null,
    },
    type: {
      type: String as PropType<FormatterType>,
      required: true,
      validator: (value: string) => ['currency', 'datetime', 'date', 'relative-time', 'number', 'text', 'list'].includes(value),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    // Most callers pass no `code`, and formatCurrency would then default to
    // USD — wrong for every non-dollar promoter. Fall back to the promoter's
    // own region currency instead, so only callers with a per-value currency
    // need to pass one.
    const fallbackCurrencyCode = useCurrencyCode()

    // A stored currency can be an object, a stringified one or plain junk, and
    // Intl throws a RangeError on anything that is not a valid code — which
    // the catch below would turn into a raw unformatted number. The shared
    // resolver normalizes and ISO-checks both the value's own code and the
    // region fallback.
    const currencyCode = computed<string | undefined>(
      () => resolveCurrencyCode(props.options?.code, fallbackCurrencyCode.value),
    )

    const formattedValue = computed(() => {
      try {
        if (props.value === null) {
          return ''
        }
        const formatOptions: FormatOptions = {
          // A stored currency can be an object, a stringified one
          // ('{"name": "AUD"}') or plain junk. Intl throws a RangeError on
          // anything that is not a valid code, and the catch below would then
          // render the raw unformatted number, so only an ISO-shaped code is
          // passed through.
          code: currencyCode.value,
          decimal_digits: props.options?.decimal_digits,
          decimals: props.options?.decimals,
          dateStyle: props.options?.dateStyle,
          timeStyle: props.options?.timeStyle,
          numeric: props.options?.numeric,
          timeZone: props.options?.timeZone,
        }

        switch (props.type) {
          case 'currency':
            return formatCurrency(Number(props.value), formatOptions)
          case 'number':
            return formatNumber(Number(props.value), formatOptions)
          case 'date':
            return formatDate(String(props.value), props.options?.timeZone) || ''
          case 'datetime':
            return formatDateTime(String(props.value), formatOptions) || ''
          case 'relative-time':
            return formatRelativeTime(String(props.value), formatOptions)
          case 'list':
            return props.value ? props.value.join(', ') : ''
          case 'text':
            return props.value ? String(props.value) : ''
          default:
            return props.value
        }
      }
      catch (error) {
        console.error('Formatting error:', error)
        return props.value
      }
    })

    const relativeTimeTooltip = computed(() => {
      if (props.type !== 'relative-time' || props.value === null) {
        return undefined
      }
      return formatDateTime(String(props.value), { dateStyle: 'medium' }) || undefined
    })

    return {
      formattedValue,
      relativeTimeTooltip,
    }
  },
})
</script>
